import { Accordion, BBSpinner, Box, Button, Flex, Text } from '@rocket/ui';
import { useEffect, useRef, useState } from 'react';
import { LuUpload } from 'react-icons/lu';
import { useFtueHost } from '../host';
import type { PlaybookRule, PlaybookSection } from '../onboarding-api';
import { getPlaybookRules, getPlaybookSections } from '../onboarding-api';
import { SampleEditsCarousel } from './SampleEditsCarousel';

function RuleCard({ rule }: { rule: PlaybookRule }) {
  const edits = rule.sampleEdits ?? (rule.sampleEdit ? [rule.sampleEdit] : []);

  return (
    <Box borderWidth="1px" borderRadius="md" p="3" bg="bg.surface" borderColor="border">
      <Flex.V gap="1">
        <Text fontWeight="600" fontSize="sm">
          {rule.name}
        </Text>
        {rule.description && (
          <Text fontSize="sm" color="fg.muted">
            {rule.description}
          </Text>
        )}
        {edits.length > 0 && <SampleEditsCarousel edits={edits} />}
      </Flex.V>
    </Box>
  );
}

interface SectionWithRules {
  section: PlaybookSection;
  rules: Array<PlaybookRule>;
}

interface Props {
  playbookId: string;
  playbookName: string;
  onBack: () => void;
  onAugment: () => void;
  onDone: () => void;
}

export function OnboardingReviewPlaybook({
  playbookId,
  playbookName,
  onBack,
  onAugment,
  onDone,
}: Props) {
  const { bbAxios } = useFtueHost();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sectionsWithRules, setSectionsWithRules] = useState<Array<SectionWithRules>>([]);
  const [unassignedRules, setUnassignedRules] = useState<Array<PlaybookRule>>([]);

  // Stable ref so the effect always uses the current axios instance without
  // re-firing when the token refreshes and bbAxios gets a new reference.
  const bbAxiosRef = useRef(bbAxios);
  bbAxiosRef.current = bbAxios;

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [sections, rules] = await Promise.all([
          getPlaybookSections(bbAxiosRef.current, playbookId),
          getPlaybookRules(bbAxiosRef.current, playbookId),
        ]);

        const sectionMap = new Map(sections.map((s) => [s._id, s]));

        const grouped: Array<SectionWithRules> = sections.map((section) => ({
          section,
          rules: [
            ...new Map(
              rules.filter((r) => r.sections?.includes(section._id)).map((r) => [r._id, r])
            ).values(),
          ],
        }));

        const unassigned = rules.filter((r) => !r.sections?.some((sid) => sectionMap.has(sid)));

        setSectionsWithRules(grouped.filter((g) => g.rules.length > 0));
        setUnassignedRules(unassigned);
      } catch {
        setError('Failed to load playbook contents. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [playbookId]);

  const totalRules =
    sectionsWithRules.reduce((sum, g) => sum + g.rules.length, 0) + unassignedRules.length;

  const accordionItems = [
    ...sectionsWithRules.map(({ section, rules }) => ({
      value: section._id,
      title: `${section.name} (${rules.length})`,
      content: (
        <Flex.V gap="2">
          {rules.map((rule) => (
            <RuleCard key={rule._id} rule={rule} />
          ))}
        </Flex.V>
      ),
    })),
    ...(unassignedRules.length > 0
      ? [
          {
            value: '__unassigned__',
            title: `Unassigned Rules (${unassignedRules.length})`,
            content: (
              <Flex.V gap="2">
                {unassignedRules.map((rule) => (
                  <RuleCard key={rule._id} rule={rule} />
                ))}
              </Flex.V>
            ),
          },
        ]
      : []),
  ];

  return (
    <Flex.V gap="5" p="6">
      <Flex.V gap="1">
        <Text fontWeight="700" fontSize="lg">
          {playbookName}
        </Text>
        {!isLoading && !error && (
          <Text fontSize="sm" color="fg.muted">
            {totalRules} rule{totalRules !== 1 ? 's' : ''} across {sectionsWithRules.length} section
            {sectionsWithRules.length !== 1 ? 's' : ''}
          </Text>
        )}
      </Flex.V>

      {isLoading && (
        <Flex.V align="center" py="8" gap="3">
          <BBSpinner />
          <Text fontSize="sm" color="fg.muted">
            Loading playbook contents…
          </Text>
        </Flex.V>
      )}

      {error && (
        <Box p="3" bg="red.subtle" borderRadius="md" borderWidth="1px" borderColor="red.muted">
          <Text fontSize="sm" color="red.fg">
            {error}
          </Text>
        </Box>
      )}

      {!isLoading && !error && accordionItems.length === 0 && (
        <Box p="4" bg="bg.subtle" borderRadius="md" borderWidth="1px" borderColor="border">
          <Text fontSize="sm" color="fg.muted">
            This playbook has no rules yet. Upload data to get started.
          </Text>
        </Box>
      )}

      {!isLoading && !error && accordionItems.length > 0 && (
        <Accordion items={accordionItems} multiple collapsible />
      )}

      <Box p="4" bg="blue.subtle" borderRadius="md" borderWidth="1px" borderColor="blue.muted">
        <Flex.V gap="3">
          <Flex.V gap="1">
            <Text fontSize="sm" fontWeight="semibold" color="blue.fg">
              Want to improve this playbook?
            </Text>
            <Text fontSize="sm" color="fg.muted">
              Upload contracts or a playbook document to extract additional rules.
            </Text>
          </Flex.V>
          <Button
            size="sm"
            variant="outline"
            colorPalette="blue"
            onClick={onAugment}
            disabled={isLoading}
          >
            <LuUpload size={14} />
            Add More Data
          </Button>
        </Flex.V>
      </Box>

      <Flex.H gap="2" justify="flex-end">
        <Button variant="ghost" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button colorPalette="gray" size="sm" onClick={() => onDone()} disabled={isLoading}>
          Use This Playbook
        </Button>
      </Flex.H>
    </Flex.V>
  );
}
